"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { Container } from "@/components/container";
import type { Stat } from "@/lib/data/types";

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function AnimatedStat({
  stat,
  active,
  delayMs,
}: {
  stat: Stat;
  active: boolean;
  delayMs: number;
}) {
  const target = Number.parseInt(stat.value, 10) || 0;
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!active) return;

    let frame = 0;
    let start: number | null = null;
    const duration = 1600;

    const tick = (timestamp: number) => {
      if (start === null) start = timestamp;
      const elapsed = timestamp - start - delayMs;

      if (elapsed < 0) {
        frame = requestAnimationFrame(tick);
        return;
      }

      const progress = Math.min(elapsed / duration, 1);
      setDisplay(Math.round(easeOutCubic(progress) * target));

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, delayMs, target]);

  return (
    <div className="flex flex-col items-center rounded-2xl border border-line bg-background px-6 py-7 text-center shadow-[var(--shadow-card)] transition-transform duration-500 hover:-translate-y-1">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-soft">
        <Image
          src={stat.icon}
          alt=""
          width={32}
          height={32}
          className="h-8 w-8 object-contain"
          unoptimized
        />
      </div>
      <p className="font-heading text-4xl font-bold tracking-tight text-ink sm:text-5xl">
        <span className="text-red tabular-nums">{display}</span>
        <span className="ml-1 text-2xl text-charcoal sm:text-3xl">{stat.unit}</span>
      </p>
      <p className="mt-3 text-sm font-semibold uppercase tracking-[0.14em] text-muted">
        {stat.label}
      </p>
    </div>
  );
}

export function StatsSection({ stats }: { stats: Stat[] }) {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="border-b border-line bg-surface py-12 sm:py-14">
      <Container>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <AnimatedStat
              key={stat.label}
              stat={stat}
              active={active}
              delayMs={index * 120}
            />
          ))}
        </div>
      </Container>
    </section>
  );
}
